import { AddonAction } from 'models/AddonAction';
import type { Custodian } from 'models/custodian';
import type { AddonSolvingMode, LoadingResult, MergeDeal } from 'models/forms/AddonFormResult';
import type { DealSplitData } from 'models/forms/DealSplitData';
import type { Action } from 'state/actions';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import type { QuoteState } from 'state/quotes/infos/infos.model';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import { addKey, patchKey } from 'state/utils/collection';
import { addRejectionError, removeRejectionError } from 'state/utils/rejectionError';
import type { Patcher } from 'state/utils/types';

import * as actionTypes from './addonForm.actionTypes';
import type {
  AddonFormLoadingStatus,
  AddonFormState,
  AddonFormTabData,
  AddonFormTabInfo,
  AddonFormTabState,
  RemunerationMode,
  SolveMode,
  TradeClientSide,
} from './addonForm.common';
import { defaultAddonSolvingModes } from './addonForm.common';

function toBackToBackType(value: string | undefined): BackToBackType | undefined {
  if (value === 'HedgingTrade' || value === 'ClientTrade' || value === 'None') {
    return value;
  }

  return undefined;
}

const patchForm = (
  state: AddonFormState,
  tabId: string,
  patcher: Patcher<Readonly<AddonFormTabData>>,
): AddonFormState => {
  return patchKey(state, tabId, tabState => {
    if (tabState.loadingStatus !== 'Success') {
      return tabState;
    }

    const form = tabState.form!;
    const patchedForm = patcher(form, tabState.info!);

    return {
      form: {
        ...form,
        ...patchedForm,
      },
    };
  });
};

export function addonFormReducer(state: AddonFormState = {}, action: Action): AddonFormState {
  switch (action.type) {
    case actionTypes.ADDON_FORM_REQUESTED:
      if (state[action.tabId]) {
        return state;
      }

      return addKey(state, action.tabId, {
        loadingStatus: 'Pending',
      });
    case actionTypes.ADDON_FORM_RESET_REQUESTED:
      return patchKey(state, action.tabId, () => ({
        loadingStatus: 'Resetting' as AddonFormLoadingStatus,
        tradeDoneExecMessages: undefined,
      }));
    case actionTypes.ADDON_FORM_RECEIVED:
    case actionTypes.ADDON_FORM_RESET:
      return patchKey(state, action.tabId, previousState => {
        const defaultTradeClientSide: TradeClientSide = action.data.tradeClientSide ?? 'buy';
        const defaultAddonAction: AddonAction = action.data.addonAction ?? 'Increase';
        const isOtc = action.data.wrapper.toLowerCase() !== 'note' && action.data.wrapper.toLowerCase() !== 'warrant';
        const isShareAccDec = ['ShareDecumulator', 'ShareAccumulator'].some(
          x => x === action.data.instrumentContractName,
        );
        const originalDealSplit = action.data.dealSplit;

        const backToBackType = toBackToBackType(action.data.backToBackType);

        const info: AddonFormTabInfo = {
          productId: action.data.productId,
          instrumentName: action.data.instrumentName,
          instrumentContractName: action.data.instrumentContractName,
          wrapper: action.data.wrapper,
          fileCode: action.data.fileCode,
          isinCode: action.data.isinCode,
          isOtc,
          isShareAccDec,
          assetTypes: action.data.assetTypes,
          originalQuoteId: action.data.quoteId,
          currency: action.data.currency,
          quoteState: undefined,
          isPriceInconsistent: false,
          initialTradeSide: action.data.initialTradeSide,
          isBackToBack: action.data.isBackToBack,
          backToBackType,
          isDelayedBackToBack: action.data.isDelayedBackToBack,
        };

        const withoutNotional = (dealSplit: readonly DealSplitData[]): readonly DealSplitData[] =>
          dealSplit.map(line => ({ ...line, NotionalAmountInGrid: undefined }));

        const remunerationMode = action.data.remunerationMode ?? 'Upfront';
        const availableSolvingModes: readonly AddonSolvingMode[] = (
          action.data.availableSolvingModes || defaultAddonSolvingModes
        ).map(s => {
          if (s === 'Xmargin') {
            return 'XMargin';
          }
          return s;
        });
        const solveModes = getSolvesModes(availableSolvingModes, remunerationMode);
        const solveFor = solveModes !== undefined ? solveModes[0] : 'UpfrontFee';

        const form: AddonFormTabData = {
          availableSolvingModes,
          backToBackFileCode: action.data.backToBackFileCode,
          backToBackIsBackToBack: action.data.isBackToBack,
          backToBackIsDelayedBackToBack: action.data.isDelayedBackToBack,
          backToBackIsDeltaAdjusted: action.data.isDeltaAdjusted,
          backToBackNoteNominal: action.data.noteNominal,
          backToBackType,
          notionalType: action.data.notionalType,
          custodians: action.data.custodians,
          dealSplit: isOtc ? withoutNotional(originalDealSplit) : getDealSplit(action.data.custodians),
          tradeClientSide: defaultTradeClientSide,
          addonAction: defaultAddonAction,
          mergeDealsRequested: previousState.form?.mergeDealsRequested ?? true,
          mergeableDeals: previousState.form?.mergeableDeals,
          selectedTradeReference: previousState.form?.selectedTradeReference,
          remunerationMode,
          selectedOnBehalfClient: action.data.selectedOnBehalfClient,
          solveModes,
          solveFor,
          totalNumberOfAccumulationDays: action.data.totalNumberOfAccumulationDays,
          isReadOnly: false,
          fundingLockDate: action.data.fundingLockDate,
          salesDesk: action.data.salesDesk,
          salesBeneficiary: action.data.salesBeneficiary,
          availableSalesBeneficiaries: action.data.availableSalesBeneficiaries,
          availableSalesDesks: action.data.availableSalesDesks,
        };

        const tabState: AddonFormTabState = {
          form,
          info,
          loadingStatus: 'Success',
        };

        return tabState;
      });
    case actionTypes.ADDON_QUOTE_REQUESTED:
      const resetQuoteState = patchKey(state, action.tabId, s =>
        s.info ? { ...s, info: { ...s.info, quoteState: undefined, isPriceInconsistent: false } } : s,
      );
      return patchForm(resetQuoteState, action.tabId, _form => {
        return {
          isReadOnly: true,
        };
      });
    case actionTypes.ADDON_QUOTE_STALE:
      return patchKey(state, action.tabId, s =>
        s.info && s.form
          ? { ...s, info: { ...s.info, quoteState: 'Previous' as QuoteState }, form: { ...s.form, isReadOnly: false } }
          : s,
      );
    case actionTypes.ADDON_QUOTE_PRICE_INCONSISTENT:
      return patchKey(state, action.tabId, s =>
        s.info ? { ...s, info: { ...s.info, isPriceInconsistent: true } } : s,
      );
    case actionTypes.ADDON_QUOTE_REQUEST_FAILED:
    case actionTypes.ADDON_QUOTE_TRADE_FAILED:
    case actionTypes.ADDON_QUOTE_TRADE_REJECTED:
    case actionTypes.ADDON_QUOTE_CANCELLED:
      return patchForm(state, action.tabId, _form => {
        return {
          isReadOnly: false,
        };
      });

    case actionTypes.ADDON_FORM_REQUEST_FAILED:
    case actionTypes.ADDON_FORM_RESET_FAILED:
      return patchKey(state, action.tabId, () => {
        const tabState: AddonFormTabState = {
          loadingStatus: 'Error',
          loadingError: action.error,
        };
        return tabState;
      });
    case actionTypes.ADDON_FORM_REQUEST_TIMED_OUT:
      return patchKey(state, action.tabId, () => {
        const tabState: AddonFormTabState = {
          loadingStatus: 'Error',
          loadingError: 'TIMEOUT',
        };
        return tabState;
      });

    case actionTypes.ADDON_NOTIONAL_CHANGED:
      return patchForm(state, action.tabId, (form, info: AddonFormTabInfo) => {
        if (form.notionalType === 'Units') {
          return state;
        }

        const newDealSplit = [...form.dealSplit];

        const hasASingleCustodian =
          form.custodians.isSuccess && form.custodians.data && form.custodians.data.length === 1;

        if ((hasASingleCustodian || info.isOtc) && newDealSplit.length === 1) {
          newDealSplit[0].NotionalAmountInGrid = action.addonNotional;
        }

        return {
          addonNotional: action.addonNotional,
          dealSplit: newDealSplit,
        };
      });
    case 'ADDON_PRIMARY_DEAL_ID_CHANGED':
      return patchForm(state, action.tabId, () => {
        return {
          addonPrimaryDealId: action.value,
        };
      });
    case actionTypes.ADDON_NOTIONAL_TYPE_CHANGED:
      return patchForm(state, action.tabId, form => {
        const newDealSplit = [...form.dealSplit];

        newDealSplit.forEach(ds => (ds.NotionalAmountInGrid = undefined));

        return {
          notionalType: action.notionalType,
          addonNotional: undefined,
          addonNotionalInUnits: undefined,
          dailyNumberOfShares: undefined,
          dealSplit: newDealSplit,
        };
      });
    case actionTypes.ADDON_DAILY_NUMBER_OF_SHARES_CHANGED:
      return patchForm(state, action.tabId, form => {
        if (form.notionalType === 'Currency') {
          return form;
        }

        const notionalInUnits = computeNotionalInUnits(action.dailyNumberOfShares, form.totalNumberOfAccumulationDays);

        const newDealSplit = [...form.dealSplit];
        newDealSplit[0].NotionalAmountInGrid = notionalInUnits;

        return {
          dailyNumberOfShares: action.dailyNumberOfShares,
          addonNotionalInUnits: notionalInUnits,
          dealSplit: newDealSplit,
        };
      });
    case actionTypes.ADDON_CLIENT_CHANGED:
      return patchForm(state, action.tabId, () => ({
        selectedOnBehalfClient: action.selectedOnBehalfClient,
      }));
    case actionTypes.ADDON_DEAL_SPLIT_CHANGED:
      return patchForm(state, action.tabId, () => ({
        dealSplit: action.dealSplit,
      }));
    case actionTypes.ADDON_TRADE_CLIENT_SIDE_CHANGED:
      return patchForm(state, action.tabId, form => ({
        tradeClientSide: action.tradeClientSide,
        mergeDealsRequested: true,
        selectedTradeReference: form.selectedTradeReference ?? getTradeReference(form.mergeableDeals),
      }));
    case actionTypes.ADDON_ACTION_CHANGED:
      return patchForm(state, action.tabId, form => ({
        addonAction: action.addonAction,
        mergeDealsRequested: true,
        selectedTradeReference: form.selectedTradeReference ?? getTradeReference(form.mergeableDeals),
      }));
    case actionTypes.ADDON_MERGE_DEALS_REQUESTED_CHANGED:
      return patchForm(state, action.tabId, form => ({
        mergeDealsRequested: action.mergeDealsRequested,
        selectedTradeReference: action.mergeDealsRequested ? getTradeReference(form.mergeableDeals) : undefined,
      }));
    case actionTypes.ADDON_SELECTED_TRADE_REFERENCE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        selectedTradeReference: action.selectedTradeReference,
      }));
    case actionTypes.ADDON_CUSTODIANS_RELOADED:
      return patchForm(state, action.tabId, () => {
        const custodians = { isSuccess: true, data: action.custodians };
        return {
          custodians,
          dealSplit: getDealSplit(custodians),
        };
      });
    case actionTypes.ADDON_CUSTODIANS_RELOAD_FAILED:
      return patchForm(state, action.tabId, () => ({
        custodians: { isSuccess: false, error: action.error },
        dealSplit: [],
      }));
    case actionTypes.ADDON_SALES_BENEFICIARIES_RELOADED:
      return patchForm(state, action.tabId, () => ({
        availableSalesBeneficiaries: action.salesBeneficiaries,
      }));
    case actionTypes.ADDON_SALES_DESKS_RELOADED:
      return patchForm(state, action.tabId, () => ({
        availableSalesDesks: action.salesDesks,
      }));
    case actionTypes.ADDON_SALES_DESK_CHANGED:
      return patchForm(state, action.tabId, () => ({
        salesDesk: action.salesDesk,
        availableSalesBeneficiaries: [],
      }));
    case actionTypes.ADDON_SALES_BENEFICIARIES_CHANGED:
      return patchForm(state, action.tabId, () => ({
        salesBeneficiary: action.salesBeneficiary,
        availableSalesDesks: [],
      }));
    case actionTypes.ADDON_REMUNERATION_MODE_CHANGED:
      return patchForm(state, action.tabId, form => {
        const availableSolvingModes = form.availableSolvingModes || defaultAddonSolvingModes;
        const solveModes = getSolvesModes(availableSolvingModes, action.remunerationMode);
        const nextSelectedSolveMode = getNextSelectedSolveMode(solveModes, form.solveFor);

        return {
          remunerationMode: action.remunerationMode,
          solveModes,
          solveFor: nextSelectedSolveMode,
        };
      });
    case actionTypes.ADDON_SOLVE_MODE_CHANGED:
      return patchForm(state, action.tabId, tabData => ({
        solveFor: action.solveMode,
        xMargin: action.solveMode === 'XMargin' ? undefined : tabData.xMargin,
        upFrontFee: action.solveMode === 'UpfrontFee' ? undefined : tabData.upFrontFee,
        offerPrice: action.solveMode === 'OfferPrice' ? undefined : tabData.offerPrice,
        fixedAmount: action.solveMode === 'FixedAmount' ? undefined : tabData.fixedAmount,
      }));
    case actionTypes.ADDON_X_MARGIN_CHANGED:
      return patchForm(state, action.tabId, () => ({
        xMargin: action.xMargin,
      }));
    case actionTypes.ADDON_UPFRONT_FEE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        upFrontFee: action.upFrontFee,
      }));
    case actionTypes.ADDON_OFFER_PRICE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        offerPrice: action.offerPrice,
      }));
    case actionTypes.ADDON_FIXED_AMOUNT_CHANGED:
      return patchForm(state, action.tabId, () => ({
        fixedAmount: action.fixedAmount,
      }));
    case actionTypes.ADDON_FUNDING_LOCK_DATE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        fundingLockDate: action.fundingLockDate,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_IS_BACK_TO_BACK_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackIsBackToBack: action.isBackToBack,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_TYPE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackType: action.backToBackType,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_FILE_CODE_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackFileCode: action.backToBackFileCode,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_NOTE_NOMINAL_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackNoteNominal: action.backToBackNoteNominal,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_IS_DELTA_ADJUSTED_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackIsDeltaAdjusted: action.backToBackIsDeltaAdjusted,
      }));
    case actionTypes.ADDON_BACK_TO_BACK_IS_DELAYED_BACK_TO_BACK_CHANGED:
      return patchForm(state, action.tabId, () => ({
        backToBackIsDelayedBackToBack: action.backToBackIsDelayedBackToBack,
      }));

    case actionTypes.ADDON_QUOTE_ACKED:
      return patchForm(state, action.tabId, () => ({
        lastQuoteId: action.quoteId,
        lastQuoteExternalId: action.externalQuoteId,
      }));

    case actionTypes.ADDON_QUOTE_RECEIVED:
      return patchKey(state, action.tabId, addonTabState => {
        const tabState: AddonFormTabState = {
          ...addonTabState,
          quoteDetails: action.quote.quoteDetails,
        };
        if (tabState.form && action.quote?.status !== 'Quoted') {
          tabState.form.isReadOnly = false;
        }
        return tabState;
      });
    case actionTypes.ADDON_QUOTE_REMOVED:
      if (state[action.tabId] === undefined) {
        return state;
      }

      return patchKey(state, action.tabId, addonTabState => {
        const tabState: AddonFormTabState = {
          ...addonTabState,
          form: {
            ...addonTabState.form!,
            lastQuoteId: undefined,
            lastQuoteExternalId: undefined,
          },
          quoteDetails: undefined,
          info: addonTabState.info
            ? {
                ...addonTabState.info,
                quoteState: undefined,
              }
            : addonTabState.info,
        };
        return tabState;
      });

    case actionTypes.ADDON_MERGEABLE_DEALS_LOADED:
      return patchKey(state, action.tabId, addonTabState => {
        const loadingResult: LoadingResult<ReadonlyArray<MergeDeal>> = { isSuccess: true, data: action.mergeableDeals };

        const isNote = !(addonTabState.info?.isOtc ?? false);
        const isNotEmpty = action.mergeableDeals.length !== 0;

        const formData: AddonFormTabData = addonTabState.form ?? ({} as Partial<AddonFormTabData> as AddonFormTabData);

        const mergeDealsRequested = (isNote && isNotEmpty) || !isNote;

        const newFormData = {
          mergeableDeals: loadingResult,
          mergeDealsRequested,
          selectedTradeReference: getTradeReference(loadingResult),
        } as Partial<AddonFormTabData> as AddonFormTabData;

        const tabState: AddonFormTabState = {
          ...addonTabState,
          form: {
            ...formData,
            ...newFormData,
          },
          quoteDetails: undefined,
        };
        return tabState;
      });

    case actionTypes.ADDON_MERGEABLE_DEALS_LOAD_REQUEST_FAILED:
      return patchKey(state, action.tabId, addonTabState => {
        const loadingResult: LoadingResult<ReadonlyArray<MergeDeal>> = { isSuccess: false, error: action.error };

        const formData: AddonFormTabData = addonTabState.form ?? ({} as Partial<AddonFormTabData> as AddonFormTabData);

        const newFormData = {
          mergeableDeals: loadingResult,
          selectedTradeReference: undefined,
        } as Partial<AddonFormTabData> as AddonFormTabData;

        const tabState: AddonFormTabState = {
          ...addonTabState,
          form: {
            ...formData,
            ...newFormData,
          },
          quoteDetails: undefined,
        };
        return tabState;
      });
    case actionTypes.ADDON_MERGEABLE_DEALS_LOAD_REQUEST_TIMED_OUT:
      return patchKey(state, action.tabId, addonTabState => {
        const loadingResult: LoadingResult<ReadonlyArray<MergeDeal>> = { isSuccess: false, error: 'TIMEOUT' };

        const formData: AddonFormTabData = addonTabState.form ?? ({} as Partial<AddonFormTabData> as AddonFormTabData);

        const newFormData = {
          mergeDealsRequested:
            addonTabState.info?.isOtc ||
            addonTabState.form?.tradeClientSide === 'sell' ||
            addonTabState.form?.addonAction === 'Decrease',
          mergeableDeals: loadingResult,
          selectedTradeReference: undefined,
        } as Partial<AddonFormTabData> as AddonFormTabData;

        const tabState: AddonFormTabState = {
          ...addonTabState,
          form: {
            ...formData,
            ...newFormData,
          },
          quoteDetails: undefined,
        };
        return tabState;
      });
    case actionTypes.ADDON_QUOTE_TRADE_REQUEST_REJECTED:
      return patchForm(state, action.tabId, previousState => ({
        tradeRejectionValidationErrors: addRejectionError(
          previousState.tradeRejectionValidationErrors ?? [],
          action.error,
        ),
      }));
    case actionTypes.ADDON_QUOTE_TRADE_REQUESTED:
      return patchForm(state, action.tabId, previousState => ({
        tradeRejectionValidationErrors: removeRejectionError(previousState.tradeRejectionValidationErrors ?? []),
      }));
    case actionTypes.ADDON_QUOTE_TRADE_DONE:
      return patchForm(state, action.tabId, () => ({
        tradeDoneExecMessages: getTradeDoneExecMessages(action.execMessages),
      }));
  }
  return state;
}

const getTradeDoneExecMessages = (execMessages?: readonly string[]): readonly QuoteFormError[] => {
  return execMessages !== undefined
    ? execMessages.map(msg => {
        return {
          message: msg,
          severity: 'Warning',
          fields: [],
          values: {},
        };
      })
    : [];
};

const computeNotionalInUnits = (
  dailyNumberOfShares: number | undefined,
  totalNumberOfAccumulationDays: number | undefined,
) => {
  if (dailyNumberOfShares === undefined || totalNumberOfAccumulationDays === undefined) {
    return undefined;
  }
  return dailyNumberOfShares * totalNumberOfAccumulationDays;
};

const getSolvesModes = (
  availableSolvingModes: readonly AddonSolvingMode[],
  remunerationMode: RemunerationMode,
): SolveMode[] => {
  const availableSolvingModesWithoutPrice = availableSolvingModes.filter(solvingMode => solvingMode !== 'Price');
  switch (remunerationMode) {
    case 'Upfront':
      return ['UpfrontFee', ...availableSolvingModesWithoutPrice];
    case 'Reoffer':
      return ['OfferPrice', ...availableSolvingModesWithoutPrice];
    case 'Premium':
      return ['FixedAmount', ...availableSolvingModesWithoutPrice];
  }
};

const getNextSelectedSolveMode = (solveModes: SolveMode[], currentSelectedSolveMode: SolveMode): SolveMode => {
  switch (currentSelectedSolveMode) {
    case 'UpfrontFee':
    case 'OfferPrice':
    case 'FixedAmount':
    case 'XMargin':
      return solveModes[0];

    default:
      return currentSelectedSolveMode;
  }
};

const getDealSplit = ({ isSuccess, data: custodians }: LoadingResult<readonly Custodian[]>): DealSplitData[] => {
  if (!isSuccess) {
    return [{}];
  }

  if (!custodians) {
    return [{}];
  }

  if (custodians.length === 1) {
    return [
      {
        Custodian: custodians[0].id,
      },
    ];
  }

  return [{}];
};

const getTradeReference = (result: LoadingResult<ReadonlyArray<MergeDeal>> | undefined) => {
  if (result?.data?.length === 1) {
    return result.data[0].tradeReference;
  }

  return undefined;
};
